import { useEffect, useId, useState } from 'react';
import { useNavigate } from 'react-router';

import { Badge, Button, Divider, Icon } from '@ds/core';
import { Alert, Modal } from '@ds/feedback';
import { FormField, Textarea } from '@ds/forms';
import { dataSimples, moeda } from '@/comuns/formato';
import { useAviso } from '@/comuns/toast';
import { dados } from '@/data/porta';
import type { DetalheDoTitulo } from '@/data/financeiroAvulso';
import type { Movimentacao, SituacaoDoTitulo, Titulo } from '@/data/tipos';
import { SITUACAO_DA_NOTA } from '@/data/notasFiscais';
import type { NotaFiscal } from '@/data/notasFiscais';
import { mesCurto } from './Relatorios';

const SITUACAO: Record<SituacaoDoTitulo, { rotulo: string; tom: 'success' | 'warning' | 'neutral' }> = {
    aberto: { rotulo: 'Em aberto', tom: 'warning' },
    parcial: { rotulo: 'Parcial', tom: 'warning' },
    pago: { rotulo: 'Quitado', tom: 'success' },
    cancelado: { rotulo: 'Cancelado', tom: 'neutral' },
};

export interface DetalheDoTituloModalProps {
    tituloId: string;
    onFechar: () => void;
    onEditar: (detalhe: DetalheDoTitulo) => void;
    onBaixar: (titulo: Titulo) => void;
    onMudou: () => void;
}

/**
 * Detalhe do título. Editar e pagar são o que a pessoa vem buscar; o resto é
 * leitura — inclusive cada pagamento, com taxa e dia em que o dinheiro entra.
 */
export function DetalheDoTituloModal({ tituloId, onFechar, onEditar, onBaixar, onMudou }: DetalheDoTituloModalProps) {
    const aviso = useAviso();
    const navigate = useNavigate();
    const [detalhe, setDetalhe] = useState<DetalheDoTitulo | null>(null);
    const [erro, setErro] = useState<string | null>(null);
    const [cancelando, setCancelando] = useState(false);
    const [ocupado, setOcupado] = useState(false);
    /* O pagamento que está sendo estornado e o motivo — um de cada vez. */
    const [estornando, setEstornando] = useState<Movimentacao | null>(null);
    const [motivo, setMotivo] = useState('');
    const [versao, setVersao] = useState(0);
    const idMotivo = useId();
    const [notas, setNotas] = useState<NotaFiscal[] | null>(null);
    const [preparando, setPreparando] = useState(false);

    useEffect(() => {
        let vivo = true;
        dados().detalheDoTitulo(tituloId)
            .then((d) => { if (vivo) setDetalhe(d); })
            .catch((e: unknown) => { if (vivo) setErro(e instanceof Error ? e.message : 'Não foi possível abrir o título.'); });
        return () => { vivo = false; };
    }, [tituloId, versao]);

    /* As notas do título. Quem não tem o financeiro nem chega aqui; e erro de
       leitura não esconde o título — só a seção fica sem as notas. */
    useEffect(() => {
        let vivo = true;
        dados().notasFiscais({ origem: 'clinica', tituloId })
            .then((n) => { if (vivo) setNotas(n); })
            .catch(() => { if (vivo) setNotas([]); });
        return () => { vivo = false; };
    }, [tituloId, versao]);

    const prepararNota = async () => {
        setPreparando(true);
        try {
            const { id } = await dados().prepararNotaDoTitulo(tituloId);
            onFechar();
            void navigate({ pathname: '/app/financeiro/notas', search: `?nota=${id}` });
        } catch (e) {
            aviso({ title: 'Não foi possível preparar a nota', description: e instanceof Error ? e.message : undefined, tone: 'danger' });
        } finally {
            setPreparando(false);
        }
    };

    const estornar = async () => {
        if (!estornando || !motivo.trim()) return;
        setOcupado(true);
        try {
            await dados().estornarPagamento(estornando.id, motivo);
            aviso({ title: 'Pagamento estornado', description: `${moeda(estornando.valorCentavos)} · ${dataSimples(estornando.pagoEm)}`, tone: 'warning' });
            setEstornando(null);
            setMotivo('');
            setVersao((v) => v + 1);
            onMudou();
        } catch (e) {
            aviso({ title: 'Não foi possível estornar', description: e instanceof Error ? e.message : 'Tente de novo.', tone: 'danger' });
        } finally {
            setOcupado(false);
        }
    };

    const cancelar = async () => {
        if (!detalhe) return;
        setOcupado(true);
        try {
            await dados().cancelarTitulo(detalhe.titulo.id);
            aviso({ title: 'Título cancelado', description: detalhe.titulo.descricao, tone: 'warning' });
            onMudou();
            onFechar();
        } catch (e) {
            aviso({ title: 'Não foi possível cancelar', description: e instanceof Error ? e.message : 'Tente de novo.', tone: 'danger' });
        } finally {
            setOcupado(false);
        }
    };

    const t = detalhe?.titulo;
    const emAberto = t ? t.situacao === 'aberto' || t.situacao === 'parcial' : false;

    return (
        <Modal
            open width={580} onClose={onFechar}
            title={t?.descricao ?? 'Título'}
            description={t ? `${t.tipo === 'pagar' ? 'Conta a pagar' : 'Título a receber'} · vencimento ${dataSimples(t.vencimento)}` : undefined}
            footer={t && (
                <>
                    {t.situacao !== 'cancelado' && detalhe.pagamentos.length === 0 && !cancelando && (
                        <Button variant="ghost" onClick={() => setCancelando(true)}>Cancelar título</Button>
                    )}
                    <span style={{ marginLeft: 'auto' }} />
                    {t.contatoId && (
                        <Button variant="ghost" onClick={() => { onFechar(); void navigate(`/app/contatos/${t.contatoId}`); }}>Abrir paciente</Button>
                    )}
                    {t.situacao !== 'cancelado' && (
                        <Button variant="secondary" iconLeft={<Icon name="pencil" size={15} />} onClick={() => onEditar(detalhe)}>Editar</Button>
                    )}
                    {emAberto && (
                        <Button iconLeft={<Icon name="circle-check" size={15} />} onClick={() => onBaixar(t)}>
                            {t.tipo === 'pagar' ? 'Pagar' : 'Receber'}
                        </Button>
                    )}
                </>
            )}
        >
            {erro && <Alert tone="danger" title="Não foi possível abrir o título">{erro}</Alert>}
            {!erro && !t && <p style={{ margin: 0, fontSize: 13.5, color: 'var(--text-secondary)' }}>Carregando…</p>}
            {t && detalhe && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                    {cancelando && (
                        <Alert
                            tone="danger" title="Cancelar este título?"
                            action={(
                                <span style={{ display: 'flex', gap: 8 }}>
                                    <Button size="sm" variant="ghost" onClick={() => setCancelando(false)}>Manter</Button>
                                    <Button size="sm" variant="danger" onClick={() => { void cancelar(); }} disabled={ocupado}>Cancelar título</Button>
                                </span>
                            )}
                        >
                            Ele sai de contas a {t.tipo} e do relatório de competência. Não há pagamento para desfazer.
                        </Alert>
                    )}

                    <div style={{ display: 'flex', alignItems: 'baseline', gap: 12, flexWrap: 'wrap', padding: '14px 16px', background: 'var(--surface-secondary)', border: '1px solid var(--border-subtle)', borderRadius: 'var(--radius-md)' }}>
                        <span style={{ fontFamily: 'var(--font-ui)', fontSize: 13, color: 'var(--text-secondary)' }}>Valor</span>
                        <span style={{ marginLeft: 'auto', fontFamily: 'var(--font-ui)', fontSize: 22, fontWeight: 700, fontVariantNumeric: 'tabular-nums', color: 'var(--text-primary)' }}>{moeda(t.valorCentavos)}</span>
                        <Badge tone={SITUACAO[t.situacao].tom} dot>{SITUACAO[t.situacao].rotulo}</Badge>
                    </div>
                    {t.pagoCentavos > 0 && t.pagoCentavos < t.valorCentavos && (
                        <p style={{ margin: 0, fontSize: 13, color: 'var(--text-secondary)' }}>
                            Pago {moeda(t.pagoCentavos)} · falta {moeda(t.valorCentavos - t.pagoCentavos)}
                        </p>
                    )}

                    <dl style={{ margin: 0, display: 'grid', gridTemplateColumns: 'auto 1fr', gap: '10px 20px', fontSize: 13 }}>
                        <dt style={{ color: 'var(--text-secondary)' }}>{t.tipo === 'pagar' ? 'Fornecedor' : 'Paciente'}</dt>
                        <dd style={{ margin: 0, textAlign: 'right', fontWeight: 500 }}>{t.contraparte}</dd>
                        <dt style={{ color: 'var(--text-secondary)' }}>Categoria</dt>
                        <dd style={{ margin: 0, textAlign: 'right', fontWeight: 500 }}>{t.categoria ?? '—'}</dd>
                        <dt style={{ color: 'var(--text-secondary)' }}>Competência</dt>
                        <dd style={{ margin: 0, textAlign: 'right', fontWeight: 500 }}>{mesCurto(t.competencia.slice(0, 7))}</dd>
                        <dt style={{ color: 'var(--text-secondary)' }}>Parcela</dt>
                        <dd style={{ margin: 0, textAlign: 'right', fontWeight: 500 }}>{t.parcela} de {t.parcelasTotal}</dd>
                        {detalhe.observacoes && (
                            <>
                                <dt style={{ color: 'var(--text-secondary)' }}>Observações</dt>
                                <dd style={{ margin: 0, textAlign: 'right', fontWeight: 500 }}>{detalhe.observacoes}</dd>
                            </>
                        )}
                    </dl>

                    {estornando && (
                        <Alert
                            tone="danger" title={`Estornar ${moeda(estornando.valorCentavos)} de ${dataSimples(estornando.pagoEm)}?`}
                            action={(
                                <span style={{ display: 'flex', gap: 8 }}>
                                    <Button size="sm" variant="ghost" onClick={() => { setEstornando(null); setMotivo(''); }}>Manter</Button>
                                    <Button size="sm" variant="danger" onClick={() => { void estornar(); }} disabled={ocupado || !motivo.trim()}>Estornar</Button>
                                </span>
                            )}
                        >
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                                <span>O pagamento sai do extrato e do saldo, e o título volta a ficar em aberto no valor estornado. Uma cópia fica no histórico com o motivo.</span>
                                <FormField label="Motivo" required htmlFor={idMotivo}>
                                    <Textarea id={idMotivo} rows={2} value={motivo} onChange={setMotivo} placeholder="Baixa lançada no título errado" />
                                </FormField>
                            </div>
                        </Alert>
                    )}

                    <Divider label="Pagamentos" spacing={2} />
                    {detalhe.pagamentos.length === 0 ? (
                        <p style={{ margin: 0, fontSize: 13, color: 'var(--text-secondary)' }}>Nenhum pagamento registrado.</p>
                    ) : (
                        <ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'flex', flexDirection: 'column' }}>
                            {detalhe.pagamentos.map((p) => (
                                <li key={p.id} style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap', padding: '9px 0', borderBottom: '1px solid var(--border-subtle)', fontSize: 13 }}>
                                    <span style={{ width: 84, color: 'var(--text-tertiary)', fontVariantNumeric: 'tabular-nums' }}>{dataSimples(p.pagoEm)}</span>
                                    <span style={{ flex: 1, minWidth: 140, color: 'var(--text-body)' }}>
                                        {[p.metodo, p.conta].filter(Boolean).join(' · ') || 'Baixa manual'}
                                        {p.taxaCentavos > 0 && <span style={{ color: 'var(--text-tertiary)' }}> · taxa {moeda(p.taxaCentavos)}</span>}
                                    </span>
                                    <span style={{ fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>{moeda(p.valorCentavos)}</span>
                                    {!estornando && (
                                        <Button size="sm" variant="ghost" onClick={() => { setEstornando(p); setMotivo(''); }} aria-label={`Estornar pagamento de ${moeda(p.valorCentavos)} em ${dataSimples(p.pagoEm)}`}>
                                            Estornar
                                        </Button>
                                    )}
                                </li>
                            ))}
                        </ul>
                    )}

                    {t.tipo === 'receber' && t.situacao !== 'cancelado' && (
                        <>
                            <Divider label="Nota fiscal" spacing={2} />
                            {(notas ?? []).length > 0 && (
                                <ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'flex', flexDirection: 'column' }}>
                                    {(notas ?? []).map((n) => (
                                        <li key={n.id}>
                                            <button
                                                type="button"
                                                onClick={() => { onFechar(); void navigate({ pathname: '/app/financeiro/notas', search: `?nota=${n.id}` }); }}
                                                style={{ display: 'flex', alignItems: 'center', gap: 12, width: '100%', padding: '9px 0', border: 0, borderBottom: '1px solid var(--border-subtle)', background: 'transparent', cursor: 'pointer', textAlign: 'left', fontFamily: 'var(--font-ui)', fontSize: 13, color: 'var(--text-body)' }}
                                            >
                                                <Badge tone={SITUACAO_DA_NOTA[n.situacao].tom} dot>{SITUACAO_DA_NOTA[n.situacao].rotulo}</Badge>
                                                <span style={{ flex: 1 }}>{n.numero ? `Nota ${n.numero}` : n.pendencias.length ? `${n.pendencias.length} ${n.pendencias.length === 1 ? 'pendência' : 'pendências'}` : 'Pronta'}</span>
                                                <span style={{ fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>{moeda(n.valorServicosCentavos)}</span>
                                            </button>
                                        </li>
                                    ))}
                                </ul>
                            )}
                            {notas !== null && notas.filter((n) => n.situacao !== 'cancelada' && n.situacao !== 'rejeitada')
                                .reduce((s, n) => s + n.valorServicosCentavos, 0) < t.valorCentavos && (
                                <div>
                                    <Button size="sm" variant="secondary" iconLeft={<Icon name="file-plus" size={14} />} onClick={() => { void prepararNota(); }} loading={preparando}>
                                        Preparar nota fiscal
                                    </Button>
                                </div>
                            )}
                        </>
                    )}

                    {(detalhe.estornos?.length ?? 0) > 0 && (
                        <>
                            <Divider label="Estornados" spacing={2} />
                            <ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'flex', flexDirection: 'column' }}>
                                {detalhe.estornos?.map((e) => (
                                    <li key={e.id} style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap', padding: '9px 0', borderBottom: '1px solid var(--border-subtle)', fontSize: 13, color: 'var(--text-secondary)' }}>
                                        <span style={{ width: 84, fontVariantNumeric: 'tabular-nums' }}>{dataSimples(e.pagoEm)}</span>
                                        <span style={{ flex: 1, minWidth: 140 }}>
                                            Estornado em {dataSimples(e.estornadoEm.slice(0, 10))}{e.motivo ? ` · ${e.motivo}` : ''}
                                        </span>
                                        <span style={{ fontVariantNumeric: 'tabular-nums', textDecoration: 'line-through' }}>{moeda(e.valorCentavos)}</span>
                                    </li>
                                ))}
                            </ul>
                        </>
                    )}
                </div>
            )}
        </Modal>
    );
}
